"use client";

import * as React from "react";
import {
  UserSessionsError,
  UserSessionsLoading,
  UserSessions as UserSessionsPresentational,
} from "./lib/user-sessions.js";
import type {
  UserSessionsErrorProps,
  UserSessionsLoadingProps,
} from "./lib/user-sessions.js";
import { useSessions } from "./api/endpoint.js";
import { ApiProvider, useApi, useApiReady } from "./api/api-provider.js";
import { useWorkOsApiUrl } from "./lib/widgets-context.js";
import { ErrorBoundary } from "./lib/error-boundary.js";
import { useQuery, skipToken } from "@tanstack/react-query";
import { getClaims } from "./api/utils.js";
import { WidgetRootDomProps } from "./lib/utils.js";

type UserSessionsWithoutCurrentSessionIdProps = {
  authToken: () => Promise<string>;
  currentSessionId?: never;
};

type UserSessionsWithCurrentSessionIdProps = {
  authToken: string;
  currentSessionId: string;
};

type UserSessionsProps = WidgetRootDomProps &
  (
    | UserSessionsWithoutCurrentSessionIdProps
    | UserSessionsWithCurrentSessionIdProps
  );

const UserSessions: React.FC<UserSessionsProps> = ({
  authToken,
  ...passthroughProps
}) => {
  const baseUrl = useWorkOsApiUrl();
  return (
    <ErrorBoundary
      fallbackRender={({ error }) => {
        const { currentSessionId, ...domProps } = passthroughProps;
        return <UserSessionsError error={error} {...domProps} />;
      }}
    >
      <ApiProvider
        widgetType="user-sessions"
        authToken={authToken}
        baseUrl={baseUrl}
      >
        <UserSessionsImpl {...passthroughProps} />
      </ApiProvider>
    </ErrorBoundary>
  );
};

interface UserSessionsImplProps extends WidgetRootDomProps {
  currentSessionId?: string;
}

const UserSessionsImpl: React.FC<UserSessionsImplProps> = ({
  currentSessionId,
  ...domProps
}) => {
  const isApiReady = useApiReady();
  const sessionsQuery = useSessions();
  const currentSessionIdQuery = useCurrentSessionIdQuery(currentSessionId);

  if (
    !isApiReady ||
    sessionsQuery.isLoading ||
    currentSessionIdQuery.isLoading
  ) {
    return <UserSessionsLoading {...domProps} />;
  }

  if (sessionsQuery.isError || currentSessionIdQuery.isError) {
    return (
      <UserSessionsError
        error={sessionsQuery.error || currentSessionIdQuery.error}
        {...domProps}
      />
    );
  }

  return (
    <ErrorBoundary
      fallbackRender={({ error }) => (
        <UserSessionsError error={error} {...domProps} />
      )}
    >
      <UserSessionsPresentational
        sessionsData={sessionsQuery.data?.data || []}
        currentSessionId={currentSessionIdQuery.data ?? ""}
        {...domProps}
      />
    </ErrorBoundary>
  );
};

function useCurrentSessionIdQuery(currentSessionId?: string) {
  const { authToken } = useApi();

  return useQuery({
    queryKey: ["authToken", authToken],
    queryFn: authToken
      ? async () => {
          if (currentSessionId) {
            return currentSessionId;
          }

          const { sid } = getClaims(authToken);
          return sid;
        }
      : skipToken,
  });
}

export type {
  UserSessionsProps,
  UserSessionsLoadingProps,
  UserSessionsErrorProps,
};
export { UserSessions, UserSessionsLoading, UserSessionsError };
